import React, { useState } from 'react';
import { Icon, Popconfirm } from 'antd';
import classnames from 'classnames';
import { useMutation } from '@apollo/client';

import { dayFormatType, formatDay } from '~helpers/day';
import { PMSStatus, PMSStatusEnum } from './constants';
import { MUTATION_UPDATE_PMS_LANDLORD } from '../../query';

import { Landlord } from 'common/components/svgs';
import ChoosePmsModal from './choose-pms-modal';

export default function PMSChannel({ t, landlord, getLandlord }) {
  const [visible, setVisible] = useState(false);
  const landlordNotVerified = landlord?.status !== 'VERIFIED';
  const isInOperation = landlord?.pms?.status === 'AT_WORK';

  const [updatePMSStatus] = useMutation(MUTATION_UPDATE_PMS_LANDLORD, {
    onCompleted: res => {
      if (res?.updatePMSLandlord?.landlord?.id) {
        getLandlord();
      }
    },
  });

  const handleOnOpenModal = () => {
    if (landlordNotVerified) return;
    setVisible(true);
  };

  const handleUpdatePMSStatus = status => {
    const variables = {
      input: {
        landlordId: landlord?.id,
        status,
      },
    };

    updatePMSStatus({ variables });
  };

  return (
    <div className="pms-channel">
      <p className="title">{t('cms.landlord.detail.token.pms_channel_integration')}</p>
      <div className="integration-status-wrapper">
        <p className="status-title">
          {t('cms.landlord.detail.token.pms_channel_integration.notice.integration_status')}
        </p>
        {(landlordNotVerified || landlord?.pms?.status === PMSStatusEnum.PAUSED) && (
          <div className="verified-notice">
            <Icon type="exclamation-circle" theme="filled" className="verified-icon" />
            <div className="notice-wrapper">
              {landlordNotVerified && (
                <span className="notice">
                  {t('cms.landlord.detail.token.pms_channel_integration.notice.not_verified')}
                </span>
              )}
              {landlord?.pms?.status === PMSStatusEnum.PAUSED && (
                <span className="notice">
                  {t('cms.landlord.detail.token.pms_channel_integration.notice.is_paused')}
                </span>
              )}
            </div>
          </div>
        )}
        <div
          className={classnames(
            'status-wrapper',
            landlord?.pms?.status === PMSStatusEnum.PAUSED && 'status-wrapper__paused',
          )}
        >
          <div className="left">
            <div className="landlord-wrapper">
              <div className={classnames('icon-wrapper', isInOperation && 'at_work_bg')}>
                <Landlord
                  className={classnames('landlord-icon', isInOperation && 'at_work_icon')}
                  hash="landlord"
                />
              </div>
              <p className="label">{landlord?.name ?? '-'}</p>
            </div>
            <div className={`divider divider__${landlord?.pms?.status}`}>
              {landlord?.pms?.status && (
                <div className={`status status__${landlord?.pms?.status}`}>
                  {PMSStatus[landlord?.pms?.status]}
                </div>
              )}
            </div>
            <div className="entrata-wrapper">
              <div className={classnames('icon-wrapper', isInOperation && 'at_work_bg')}>
                {landlord?.pms?.pms?.name ? (
                  <Icon
                    type="database"
                    theme="filled"
                    className={classnames('database-icon', isInOperation && 'at_work-icon')}
                  />
                ) : (
                  <Icon type="warning" theme="filled" className="warning-icon" />
                )}
              </div>
              <p
                className={classnames(
                  'label',
                  (landlordNotVerified || !landlord?.pms) && 'disbaled',
                )}
              >
                {!landlord?.pms ? 'Unselected PMS' : landlord?.pms?.pms?.name}
              </p>
            </div>
          </div>
          <div className="right">
            {landlord?.pms?.status && landlord?.pms?.status !== PMSStatusEnum.TERMINATED && (
              <>
                <Icon type="stop" className="stop-icon" />
                <Popconfirm
                  overlayClassName="pms-channel__popconfirm"
                  overlayStyle={{ maxWidth: 236, fontWeight: 500 }}
                  arrowPointAtCenter
                  placement="bottomLeft"
                  title={`I confirm that I want to disconncet with ${landlord?.pms?.pms?.name}`}
                  onConfirm={() => handleUpdatePMSStatus(PMSStatusEnum.TERMINATED)}
                  okText={t('cms:cms.special_offer.confirm.btn')}
                  okType="danger"
                >
                  <span className="revoke">{t('cms.landlord.token.action.revoke')}</span>
                </Popconfirm>
              </>
            )}
            {landlord?.pms?.status && landlord?.pms?.status === PMSStatusEnum.TERMINATED && (
              <>
                <Icon
                  type="api"
                  className={classnames('api-icon', landlordNotVerified && 'disbaled')}
                />
                <Popconfirm
                  overlayClassName="pms-channel__popconfirm"
                  overlayStyle={{ maxWidth: 236 }}
                  arrowPointAtCenter
                  placement="bottomLeft"
                  title="I confirm that I want to reopen the integration for this PMS"
                  onConfirm={() => handleUpdatePMSStatus(PMSStatusEnum.PENDING)}
                  okText="Confirm"
                  cancelText="Cancel"
                >
                  <span className={classnames('choose-pms', landlordNotVerified && 'disbaled')}>
                    {t('cms.landlord.detail.token.pms_channel_integration.reopen')}
                  </span>
                </Popconfirm>
              </>
            )}
            {!landlord?.pms?.status && (
              <>
                <Icon
                  type="api"
                  className={classnames('api-icon', landlordNotVerified && 'disbaled')}
                  style={{ cursor: landlordNotVerified ? 'default' : 'pointer' }}
                />
                <span
                  className={classnames('choose-pms', landlordNotVerified && 'disbaled')}
                  onClick={handleOnOpenModal}
                  style={{ cursor: landlordNotVerified ? 'default' : 'pointer' }}
                >
                  {t('cms.landlord.detail.token.pms_channel_integration.choose_pms.label')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {landlord?.pms?.status === PMSStatusEnum.TERMINATED && (
        <span className="choose-pms-word">
          {t('cms.landlord.detail.token.pms_channel_integration.or_you_can')}&nbsp;
          <span onClick={() => handleOnOpenModal(false)} className="active">
            {t('cms.landlord.detail.token.pms_channel_integration.choose_a_new_pms_channel')}
          </span>
        </span>
      )}
      <div className="created-at">
        <div className="date">
          {t('cms.landlord.detail.token.pms_channel_integration.created_at')}
        </div>
        <div className="date-value">
          {landlord?.pms?.createdAt
            ? formatDay(dayFormatType.UKFORMAT, landlord?.pms?.createdAt)
            : '-'}
        </div>
      </div>
      {visible && (
        <ChoosePmsModal
          landlord={landlord}
          getLandlord={getLandlord}
          visible={visible}
          onCancel={() => setVisible(false)}
        />
      )}
    </div>
  );
}
